"use client";

import { useEffect, useState } from "react";

// Works around a React 19.3 canary scheduling race (the build Next 16.2 ships)
// that left a ticket page deaf after a save (2026-10-28): the Server Action
// succeeded and its revalidated tree arrived, but the router's transition that
// renders it got parked for good, so the save only showed after a reload.
//
// How it gets stuck: the transition render suspends on Flight chunks of the
// streaming response ("suspended with delay"). Chunks that resolve while that
// render runs ping it synchronously from inside the render, where React drops
// the ping, and the markRootSuspended at the end of the render clears the
// pinged bit. The lane then stays in root.suspendedLanes with nothing left to
// ping it. A plain state update landing mid-render (e.g. a form closing right
// after `await action()`) makes it far more likely, hence also the
// startTransition after the await in the timeline forms.
//
// React retries suspended lanes on ANY non-idle update (markRootUpdated clears
// root.suspendedLanes), and by then every chunk is in, so the retry commits.
// This component is that update: once a second it reads the root's lane
// bitmasks, and when a transition lane has stayed suspended for two checks in
// a row, unpinged, with no render scheduled and no commit waiting, it bumps its
// own state. It reads React internals: if they aren't there (another React
// build), it does nothing. Renders nothing.

const CHECK_MS = 1000;
/** React's TransitionLanes (`lanes & 4194048` = "includes transitions"). */
const TRANSITION_LANES = 0x3fff00;

interface ReactRootInternals {
  suspendedLanes: number;
  pingedLanes: number;
  callbackNode: unknown;
  cancelPendingCommit: unknown;
}

function reactRoot(): ReactRootInternals | null {
  const doc = document as unknown as Record<string, { stateNode?: unknown } | undefined>;
  const key = Object.keys(doc).find((k) => k.startsWith("__reactContainer$"));
  const root = key ? doc[key]?.stateNode : null;
  if (!root || typeof root !== "object") return null;
  const r = root as Partial<ReactRootInternals>;
  return typeof r.suspendedLanes === "number" && typeof r.pingedLanes === "number"
    ? (r as ReactRootInternals)
    : null;
}

function hasStuckTransition(root: ReactRootInternals): boolean {
  const parked = root.suspendedLanes & TRANSITION_LANES & ~root.pingedLanes;
  return parked !== 0 && root.callbackNode == null && root.cancelPendingCommit == null;
}

export function StuckTransitionWatchdog() {
  const [, setNudges] = useState(0);
  useEffect(() => {
    const root = reactRoot();
    if (!root) return;
    let stuckChecks = 0;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      stuckChecks = hasStuckTransition(root) ? stuckChecks + 1 : 0;
      if (stuckChecks < 2) return;
      stuckChecks = 0;
      setNudges((n) => n + 1);
    }, CHECK_MS);
    return () => clearInterval(timer);
  }, []);
  return null;
}
